import { memo } from "react";
import { Avatar } from "@/components/Avatar";
import { relativeMinutes, getChannelMeta } from "@/lib/helpers";
import { Fire, Archive } from "@phosphor-icons/react";

function ConversationItem({ conv, active, onClick, currentUserId, usersById }) {
    const ch = getChannelMeta(conv.channel);
    const unread = (conv.unread_count || 0) > 0;
    const urgent = conv.urgency === "urgent";
    const isMine = conv.assigned_user_id === currentUserId;
    const assignee = conv.assigned_user_id ? usersById?.[conv.assigned_user_id] : null;
    const isArchived = !!conv.archived;

    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`conversation-item-${conv.id}`}
            data-active={active ? "true" : "false"}
            data-archived={isArchived ? "true" : "false"}
            className={
                "w-full text-left p-3 border-b border-slate-100 cursor-pointer transition-colors relative flex gap-3 items-start " +
                (active
                    ? "bg-[#F0F9FF] border-l-4 border-l-brand-600 pl-[8px]"
                    : (isArchived ? "bg-slate-50/60 hover:bg-slate-100/80 opacity-60"
                                  : "bg-white hover:bg-slate-50"))
            }
        >
            <Avatar name={conv.customer_name || "?"} size={36} />
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                    <h3 className={`font-display truncate ${unread && !isArchived ? "font-bold text-slate-900" : "font-semibold text-slate-800"} text-sm`}>
                        {conv.customer_name || "Desconocido"}
                    </h3>
                    <span className="text-[10px] text-slate-500 shrink-0">{relativeMinutes(conv.last_message_at)}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] uppercase tracking-wider font-semibold"
                          style={{ color: ch.color }}>{ch.label}</span>
                    <span className="text-[10px] text-slate-400">·</span>
                    <span className="text-[11px] text-slate-500 truncate font-medium">{conv.trip_title}</span>
                </div>
                <p className={`mt-1 text-[12px] leading-snug line-clamp-2 ${unread && !isArchived ? "text-slate-700 font-medium" : "text-slate-500"}`}>
                    {conv.last_message_preview || "—"}
                </p>
                {(urgent || isMine || assignee || conv.is_poc_phase || isArchived) && (
                    <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                        {isArchived && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600 bg-slate-200 border border-slate-300 px-1.5 py-0.5 rounded"
                                  data-testid={`archived-badge-${conv.id}`}>
                                <Archive size={10} weight="fill"/> Archivada
                            </span>
                        )}
                        {conv.is_poc_phase && (
                            <span className="text-[10px] font-bold uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded"
                                  data-testid={`poc-badge-${conv.id}`}>
                                POC
                            </span>
                        )}
                        {urgent && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
                                <Fire size={10} weight="fill" /> Urgente
                            </span>
                        )}
                        {isMine && (
                            <span className="text-[10px] font-semibold text-brand-700 bg-brand-50 px-1.5 py-0.5 rounded">
                                Tú
                            </span>
                        )}
                        {!isMine && assignee && (
                            <span className="text-[10px] text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded truncate max-w-[120px]">
                                {assignee.name.split(" ")[0]}
                            </span>
                        )}
                    </div>
                )}
            </div>
            {unread && !isArchived && (
                <span className="absolute top-4 right-3 w-2.5 h-2.5 rounded-full bg-brand-500"
                      data-testid={`unread-dot-${conv.id}`} />
            )}
        </button>
    );
}

export default memo(ConversationItem);
